import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { FileText, Save, Loader2, ArrowLeft, Edit } from 'lucide-react';
import { RichTextEditor } from '../../components/shared/RichTextEditor';

const PAGE_SLUGS = [
  { slug: 'privacy-policy', label: 'Privacy Policy' },
  { slug: 'terms-and-conditions', label: 'Terms & Conditions' },
  { slug: 'contact-us', label: 'Contact Us' },
  { slug: 'about-us', label: 'About Us' },
];

export default function PagesManagement() {
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadPages(); }, []);

  async function loadPages() {
    try {
      const { data } = await api.get('/admin/pages');
      setPages(data);
    } catch { }
  }

  async function editPage(slug) {
    try {
      const { data } = await api.get(`/admin/pages/${slug}`);
      setEditing(slug);
      setTitle(data.title || slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
      setContent(data.content || '');
    } catch {
      setEditing(slug);
      setTitle(slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
      setContent('');
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      await api.put(`/admin/pages/${editing}`, { slug: editing, title, content });
      toast.success('Page saved!');
      loadPages();
      setEditing(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <div className="space-y-4" data-testid="page-editor">
        <Button variant="ghost" onClick={() => setEditing(null)} className="mb-2" data-testid="back-to-pages-btn">
          <ArrowLeft size={16} className="mr-2" /> Back to Pages
        </Button>
        <div className="flex items-center justify-between">
          <h1 className="font-['Outfit'] text-2xl font-semibold">Edit: {title}</h1>
          <Button onClick={handleSave} disabled={saving} className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" data-testid="save-page-btn">
            {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />} Save Page
          </Button>
        </div>
        <div>
          <Label>Page Title</Label>
          <Input value={title} onChange={e => setTitle(e.target.value)} data-testid="page-title-input" />
        </div>
        <div>
          <Label>Content</Label>
          <RichTextEditor value={content} onChange={setContent} className="min-h-[300px]" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pages-management">
      <div>
        <h1 className="font-['Outfit'] text-2xl font-semibold text-[#0A0A0A] flex items-center gap-2"><FileText size={24} /> Content Pages</h1>
        <p className="text-sm text-[#52525B] mt-1">Manage your site's static pages</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {PAGE_SLUGS.map(({ slug, label }) => {
          const existing = pages.find(p => p.slug === slug);
          return (
            <Card key={slug} className="p-5 border border-zinc-200 flex items-center justify-between hover:shadow-md transition-shadow" data-testid={`page-card-${slug}`}>
              <div>
                <h3 className="font-medium text-[#0A0A0A]">{label}</h3>
                <p className="text-xs text-[#52525B] mt-1">{existing ? `Last updated: ${new Date(existing.updated_at || existing.created_at).toLocaleDateString()}` : 'Default content'}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => editPage(slug)} data-testid={`edit-page-${slug}`}>
                <Edit size={14} className="mr-1" /> Edit
              </Button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
